export type DurationObject = {
  years: number
  year: number

  months: number
  month: number

  weeks: number
  week: number

  days: number
  day: number

  hours: number
  hour: number

  minutes: number
  minute: number

  seconds: number
  second: number

  milliseconds: number
  millisecond: number
}

export type DurationUnit = keyof DurationObject

export type DurationString = `${number} ${DurationUnit}`

export type DurationLike = number | DurationString | Partial<DurationObject> | Duration

const MS_PER_SECOND = 1_000
const MS_PER_MINUTE = 60 * MS_PER_SECOND
const MS_PER_HOUR = 60 * MS_PER_MINUTE
const MS_PER_DAY = 24 * MS_PER_HOUR
const MS_PER_WEEK = 7 * MS_PER_DAY
const MS_PER_MONTH = 30 * MS_PER_DAY
const MS_PER_YEAR = 365 * MS_PER_DAY

const MS_PER_UNIT: Record<DurationUnit, number> = {
  milliseconds: 1,
  millisecond: 1,

  seconds: MS_PER_SECOND,
  second: MS_PER_SECOND,

  minutes: MS_PER_MINUTE,
  minute: MS_PER_MINUTE,

  hours: MS_PER_HOUR,
  hour: MS_PER_HOUR,

  days: MS_PER_DAY,
  day: MS_PER_DAY,

  weeks: MS_PER_WEEK,
  week: MS_PER_WEEK,

  months: MS_PER_MONTH,
  month: MS_PER_MONTH,

  years: MS_PER_YEAR,
  year: MS_PER_YEAR,
}

/**
 * A Duration class for representing time durations.
 *
 * @example
 * ```ts
 * // Create from various inputs
 * Duration.seconds(30);
 * Duration.from(5000);              // milliseconds
 * Duration.from("30 seconds");      // duration string
 * Duration.fromObject({ hours: 1, minutes: 30 });
 * Duration.fromRange(startDate, endDate);
 *
 * // Access in different units
 * const duration = Duration.minutes(90);
 * console.debug(duration.hours);   // 1.5
 * console.debug(duration.seconds); // 5400
 *
 * // Or use it directly with luxon dates
 * const now = DateTime.now();
 * const future = now.plus(duration);
 * ```
 */
export class Duration {
  /**
   * Gets the duration as milliseconds.
   */
  readonly milliseconds: number

  /**
   * Creates a new Duration instance from milliseconds.
   *
   * @param milliseconds - The duration in milliseconds
   * @throws {TypeError} When milliseconds is not a number
   */
  private constructor(milliseconds: number) {
    if (Number.isNaN(milliseconds)) {
      throw new TypeError(`Invalid milliseconds: ${milliseconds}`)
    }

    this.milliseconds = milliseconds
  }

  /**
   * Creates a Duration from various input types (number, Duration instance, or duration string).
   *
   * @example
   * ```ts
   * // From milliseconds
   * const duration1 = Duration.from(5000);
   *
   * // From existing Duration
   * const duration2 = Duration.from(Duration.minutes(5));
   *
   * // From duration string
   * const duration3 = Duration.from("30 seconds");
   * ```
   */
  static from(durationLike: DurationLike): Duration {
    if (typeof durationLike === "number") {
      return new Duration(durationLike)
    } else if (typeof durationLike === "string") {
      return Duration.fromString(durationLike)
    } else if (durationLike instanceof Duration) {
      return durationLike.copy()
    } else {
      return Duration.fromObject(durationLike)
    }
  }

  /**
   * Creates a Duration from a duration string representation.
   *
   * @example
   * ```ts
   * const duration1 = Duration.fromString("5 minutes");
   * const duration2 = Duration.fromString("30 seconds");
   * const duration3 = Duration.fromString("2.5 hours");
   * ```
   */
  static fromString(value: DurationString): Duration {
    const parts = value.trim().split(" ")
    if (parts.length !== 2) {
      throw new TypeError(`Invalid duration string format: "${value}". Expected format: "<number> <unit>"`)
    }

    const unit = parts[1]
    const amount = Number.parseFloat(parts[0])

    if (Number.isNaN(amount)) {
      throw new TypeError(`Invalid duration amount: "${parts[0]}"`)
    }

    if (!(unit in MS_PER_UNIT)) {
      throw new TypeError(`Invalid duration unit: "${unit}"`)
    }

    return Duration.fromObject({ [unit]: amount })
  }

  /**
   * Creates a Duration from an object with time unit properties.
   *
   * @throws {TypeError} When any value is not a number
   *
   * @example
   * ```ts
   * const duration = Duration.fromObject({
   *   hours: 2,
   *   minutes: 30,
   *   seconds: 15
   * });
   * ```
   */
  static fromObject(values: Partial<DurationObject>): Duration {
    let milliseconds = 0
    for (const [key, value] of Object.entries(values)) {
      if (value === undefined) {
        continue
      }

      if (Number.isNaN(value)) {
        throw new TypeError(`Invalid value for ${key}: ${value}`)
      }

      milliseconds += value * MS_PER_UNIT[key as DurationUnit]
    }

    return new Duration(milliseconds)
  }

  /**
   * Creates a Duration representing the time difference between two dates.
   *
   * @example
   * ```ts
   * const start = new Date('2023-01-01T10:00:00Z');
   * const end = new Date('2023-01-01T12:30:00Z');
   * const duration = Duration.fromRange(start, end);
   * console.debug(duration.hours); // 2.5
   * ```
   */
  static fromRange(leftDate: Date | number | string, rightDate: Date | number | string): Duration {
    const a = new Date(leftDate).getTime()
    const b = new Date(rightDate).getTime()

    if (Number.isNaN(a) || Number.isNaN(b)) {
      throw new TypeError(`Invalid date provided to fromRange, leftDate: ${leftDate} rightDate: ${rightDate}`)
    }

    const milliseconds = b - a
    return new Duration(milliseconds)
  }

  /**
   * Creates a Duration from a number of milliseconds.
   *
   * @example
   * ```ts
   * const duration = Duration.milliseconds(1500);
   * console.debug(duration.seconds); // 1.5
   * ```
   */
  static milliseconds(milliseconds: number): Duration {
    return new Duration(milliseconds)
  }

  /**
   * Creates a Duration from a number of seconds.
   *
   * @example
   * ```ts
   * const duration = Duration.seconds(90);
   * console.debug(duration.minutes); // 1.5
   * ```
   */
  static seconds(seconds: number): Duration {
    return Duration.fromObject({ seconds })
  }

  /**
   * Creates a Duration from a number of minutes.
   *
   * @example
   * ```ts
   * const duration = Duration.minutes(90);
   * console.debug(duration.hours); // 1.5
   * ```
   */
  static minutes(minutes: number): Duration {
    return Duration.fromObject({ minutes })
  }

  /**
   * Creates a Duration from a number of hours.
   *
   * @example
   * ```ts
   * const duration = Duration.hours(2.5);
   * console.debug(duration.minutes); // 150
   * ```
   */
  static hours(hours: number): Duration {
    return Duration.fromObject({ hours })
  }

  /**
   * Creates a Duration from a number of days.
   *
   * @example
   * ```ts
   * const duration = Duration.days(1.5);
   * console.debug(duration.hours); // 36
   * ```
   */
  static days(days: number): Duration {
    return Duration.fromObject({ days })
  }

  /**
   * Creates a Duration from a number of weeks.
   *
   * @example
   * ```ts
   * const duration = Duration.weeks(2);
   * console.debug(duration.days); // 14
   * ```
   */
  static weeks(weeks: number): Duration {
    return Duration.fromObject({ weeks })
  }

  /**
   * Returns a new Duration with the specified duration added.
   *
   * @example
   * ```ts
   * const duration = Duration.hours(2);
   * const result = duration.plus("30 minutes");
   * console.debug(result.hours); // 2.5
   * ```
   */
  public plus(duration: DurationLike): Duration {
    return new Duration(this.milliseconds + Duration.from(duration).milliseconds)
  }

  /**
   * Returns a new Duration with the specified duration subtracted.
   *
   * @example
   * ```ts
   * const duration = Duration.hours(2);
   * const result = duration.minus("30 minutes");
   * console.debug(result.hours); // 1.5
   * ```
   */
  public minus(duration: DurationLike): Duration {
    return new Duration(this.milliseconds - Duration.from(duration).milliseconds)
  }

  /**
   * Returns a new Duration instance with the same value.
   *
   * @example
   * ```ts
   * const original = Duration.hours(2);
   * const copy = original.copy();
   * console.debug(copy.hours); // 2
   * ```
   */
  public copy(): Duration {
    return new Duration(this.milliseconds)
  }

  /**
   * Checks if this duration is greater than another duration.
   *
   * @example
   * ```ts
   * const duration1 = Duration.hours(2);
   * const duration2 = Duration.minutes(30);
   * console.debug(duration1.isGreaterThan(duration2)); // true
   * ```
   */
  public isGreaterThan(other: DurationLike): boolean {
    return this.milliseconds > Duration.from(other).milliseconds
  }

  /**
   * Checks if this duration is less than another duration.
   *
   * @example
   * ```ts
   * const duration1 = Duration.minutes(30);
   * const duration2 = Duration.hours(2);
   * console.debug(duration1.isLessThan(duration2)); // true
   * ```
   */
  public isLessThan(other: DurationLike): boolean {
    return this.milliseconds < Duration.from(other).milliseconds
  }

  /**
   * Checks if this duration is equal to another duration.
   *
   * @example
   * ```ts
   * const duration1 = Duration.hours(2);
   * const duration2 = Duration.minutes(120);
   * console.debug(duration1.isEqualTo(duration2)); // true
   * ```
   */
  public isEqualTo(other: DurationLike): boolean {
    return this.milliseconds === Duration.from(other).milliseconds
  }

  /**
   * Checks if this duration is greater than or equal to another duration.
   *
   * @example
   * ```ts
   * const duration1 = Duration.hours(2);
   * const duration2 = Duration.minutes(120);
   * console.debug(duration1.isGreaterThanOrEqualTo(duration2)); // true
   * ```
   */
  public isGreaterThanOrEqualTo(other: DurationLike): boolean {
    return this.milliseconds >= Duration.from(other).milliseconds
  }

  /**
   * Checks if this duration is less than or equal to another duration.
   *
   * @example
   * ```ts
   * const duration1 = Duration.minutes(30);
   * const duration2 = Duration.hours(1);
   * console.debug(duration1.isLessThanOrEqualTo(duration2)); // true
   * ```
   */
  public isLessThanOrEqualTo(other: DurationLike): boolean {
    return this.milliseconds <= Duration.from(other).milliseconds
  }

  /**
   * Gets the duration as seconds (including fractional seconds).
   */
  get seconds(): number {
    return this.convert("seconds")
  }

  /**
   * Gets the duration as minutes (including fractional minutes).
   */
  get minutes(): number {
    return this.convert("minutes")
  }

  /**
   * Gets the duration as hours (including fractional hours).
   */
  get hours(): number {
    return this.convert("hours")
  }

  /**
   * Gets the duration as days (including fractional days).
   */
  get days(): number {
    return this.convert("days")
  }

  /**
   * Gets the duration as weeks (including fractional weeks).
   */
  get weeks(): number {
    return this.convert("weeks")
  }

  private convert(unit: DurationUnit): number {
    return this.milliseconds / MS_PER_UNIT[unit]
  }
}
